// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@stoneage/XDL/constants";
import { xBaseTheme } from "@stoneage/XDL/types";
import { color, styledScrollbar } from "@stoneage/XDL/Utilities";
import { StyledButton } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import MenuDefaultProps from "@insite/mobius/Menu/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";
import IconArrowRightLightSmall from "@stoneage/XDL/Icons/IconArrowRightLightSmall";

const menuItemHoverStyle = {
    megaMenu: {
        backgroundColor: "primary",
        text: "common.white",
    },
    cascadingMenu: {
        backgroundColor: "transparent",
        text: "primary",
    },
};

const buildMenuItemColorHoverRules = (type: keyof typeof menuItemHoverStyle) => {
    const style = menuItemHoverStyle[type];
    return css`
        background: ${color(style.backgroundColor)};
        ${TypographyStyle}, ${IconWrapper} {
            color: ${color(style.text)};
        }
    `;
};

export default {
    defaultProps: {
        ...MenuDefaultProps,
        mergeCss: true,
        moreIconProps: {
            src: IconArrowRightLightSmall,
            size: 12,
            color: "common.ui6",
            css: css`
                position: absolute;
                right: 8px;
                transform: translateY(-50%);
                top: 50%;
            `,
        },
        menuItemTypographyProps: {
            ...fontConfig.smallSingleLine,
            weight: fontConfig.weight.regular,
            color: "common.black",
            css: css`
                &.non-clickable {
                    font-size: 18px;
                    line-height: 21px;
                    font-weight: ${fontConfig.weight.bold};
                    border-bottom: 1px solid ${color("common.border")};
                    padding: 8px 0;
                    margin: 0 12px 8px;
                }
            `,
        },
        cssOverrides: {
            menu: css`
                &:not(.mega-menu) {
                    padding: 4px 0 12px;
                }
                &.mega-menu {
                    height: 391px;
                    max-height: 60vh;
                }

                border: 1px solid ${color("common.ui6")};
                border-top: 0;
                ${styledScrollbar(4)}

                &.mega-menu > .MenuItem[data-open] {
                    ${buildMenuItemColorHoverRules("megaMenu")};
                &:not(.mega-menu) > .MenuItem[data-open] {
                        ${buildMenuItemColorHoverRules("cascadingMenu")};
                }
            `,
            menuItem: css`
                ${StyledButton} {
                    padding: 8px 12px;
                    min-height: initial;
                }

                &.mega-menu {
                    ${StyledButton}:hover {
                        ${buildMenuItemColorHoverRules("megaMenu")};
                    }
                }
                &:not(.mega-menu) {
                    ${StyledButton}:hover {
                        ${buildMenuItemColorHoverRules("cascadingMenu")};
                    }
                }
            `,
        },
    },
} as xBaseTheme["menu"];
